import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import swal from "sweetalert";
import { Row, Col, Card, Badge, Button, Dropdown, Form, Spinner, Pagination } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import api, { getFileUrl } from "../../../api";
import CanAccess from "../../../routes/CanAccess";

const PAGE_SIZE = 9; // 3 columns × 3 rows

const money = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(Number(value || 0));

const Accounts = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [allWorkOrders, setAllWorkOrders]   = useState([]);
  const [billings, setBillings]             = useState([]);
  const [statusFilter, setStatusFilter] = usePersistentState("accounts_status", "All");
  const [projectFilter, setProjectFilter] = usePersistentState("accounts_project", searchParams.get("project") || "");
  const [workOrderFilter, setWorkOrderFilter] = usePersistentState("accounts_wo", searchParams.get("workOrder") || "");
  const [loading, setLoading]               = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = usePersistentState("accounts_page", 1);

  // Search filter state (filters WO No, Label, Project, Client, PO)
  const [search, setSearch] = useState("");
  const [poSuggestions, setPoSuggestions] = useState([]);
  const [showPoDropdown, setShowPoDropdown] = useState(false);
  const [searchingPo, setSearchingPo] = useState(false);
  const [activePoFilter, setActivePoFilter] = useState(null);
  const poDropdownRef = useRef(null);
  const poSearchTimeoutRef = useRef(null);

  /* ============================================================
     FETCH DATA — load all work orders & billings
     This enables seamless search & filter across all pages
  ============================================================ */
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [woRes, billingRes] = await Promise.all([
        api.get("/work-orders?limit=1000"),
        api.get("/billing?limit=1000"),
      ]);

      if (woRes.data?.success) {
        setAllWorkOrders(woRes.data.data || []);
      }
      if (billingRes.data?.success) {
        setBillings(billingRes.data.data || []);
      }
    } catch (err) {
      console.error("Accounts load error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Click outside to close PO suggestions dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (poDropdownRef.current && !poDropdownRef.current.contains(e.target)) {
        setShowPoDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced search for PO suggestions
  const searchPoSuggestions = (query) => {
    if (poSearchTimeoutRef.current) {
      clearTimeout(poSearchTimeoutRef.current);
    }
    poSearchTimeoutRef.current = setTimeout(async () => {
      try {
        setSearchingPo(true);
        const res = await api.get(`/purchase-orders?search=${encodeURIComponent(query.trim())}&limit=8`);
        if (res.data?.success) {
          setPoSuggestions(res.data.data || []);
          setShowPoDropdown(true);
        }
      } catch (err) {
        console.error("Error searching PO:", err);
      } finally {
        setSearchingPo(false);
      }
    }, 300);
  };

  const handleSearchChange = (val) => {
    setSearch(val);
    setCurrentPage(1);
    if (!val) {
      setActivePoFilter(null);
      setPoSuggestions([]);
      setShowPoDropdown(false);
      return;
    }
    if (val.trim().length < 2) {
      setPoSuggestions([]);
      setShowPoDropdown(false);
      return;
    }
    searchPoSuggestions(val);
  };

  const handleSelectPo = (po) => {
    setShowPoDropdown(false);
    const woId = typeof po.workOrder === "object" ? po.workOrder?._id : po.workOrder;
    if (!woId) {
      navigate(`/other-purchase-order-payments?search=${encodeURIComponent(po.poNumber || "")}`);
      return;
    }

    setActivePoFilter({
      poNumber: po.poNumber,
      poId: po._id,
      workOrderId: String(woId),
      workOrderNo: po.workOrder?.woNo || po.workOrder?.woNumber || "Linked Work Order",
      workOrderLabel: po.workOrder?.label || "",
      vendorName: po.vendor?.name || po.vendor?.companyName || "",
      totalAmount: po.totalAmount,
    });
    setSearch("");
    setCurrentPage(1);
  };

  const handlePoSearchSubmit = async () => {
    if (!search.trim()) return;
    try {
      setSearchingPo(true);
      const res = await api.get(`/purchase-orders?search=${encodeURIComponent(search.trim())}&limit=8`);
      if (res.data?.success && res.data.data?.length > 0) {
        setPoSuggestions(res.data.data);
        if (res.data.data.length === 1) {
          handleSelectPo(res.data.data[0]);
        } else {
          setShowPoDropdown(true);
        }
      }
    } catch (err) {
      console.error("Error searching PO:", err);
    } finally {
      setSearchingPo(false);
    }
  };

  const clearPoSearch = () => {
    setActivePoFilter(null);
    setCurrentPage(1);
  };

  const handleResetAllFilters = () => {
    setProjectFilter("");
    setWorkOrderFilter("");
    setStatusFilter("All");
    setSearch("");
    setPoSuggestions([]);
    setShowPoDropdown(false);
    setActivePoFilter(null);
    setCurrentPage(1);
  };

  /* ============================================================
     PROJECTS & WORK ORDERS FOR DROPDOWN FILTERS
  ============================================================ */
  const projects = useMemo(() => {
    const map = {};
    allWorkOrders.forEach((w) => {
      const pId = w.project?._id || (typeof w.project === "string" ? w.project : null);
      const pName = w.project?.projectName || w.projectName || w.project?.name;
      if (pId && pName) {
        map[pId] = pName;
      }
    });
    return Object.entries(map);
  }, [allWorkOrders]);

  const availableWorkOrders = useMemo(() => {
    if (!projectFilter) return allWorkOrders;
    return allWorkOrders.filter((w) => {
      const pId = w.project?._id || w.project;
      return String(pId) === String(projectFilter);
    });
  }, [allWorkOrders, projectFilter]);

  const poMatchedWoIds = useMemo(() => {
    const ids = new Set();
    poSuggestions.forEach((po) => {
      const woId = typeof po.workOrder === "object" ? po.workOrder?._id : po.workOrder;
      if (woId) ids.add(String(woId));
    });
    return ids;
  }, [poSuggestions]);

  /* ============================================================
     BILLING % CALCULATION — calculated across ALL work orders
  ============================================================ */
  const workOrderAccounts = useMemo(() => {
    return allWorkOrders.map((wo) => {
      const woValue = Number(wo.totalAmount || wo.value || 0);

      let raised = 0;
      billings.forEach((b) => {
        const woId = typeof b.workOrder === "object" ? b.workOrder._id : b.workOrder;
        if (woId !== wo._id) return;
        raised += Number(b.totalAmount || 0);
      });

      const percent = woValue
        ? Math.min(100, Math.round((raised / woValue) * 100))
        : 0;

      const label =
        raised >= woValue && woValue > 0
          ? "Completed"
          : raised > 0
          ? "In Progress"
          : "Pending";

      return {
        workOrder: wo._id,
        info: wo,
        total: woValue,
        completed: raised,
        percent,
        label,
      };
    });
  }, [allWorkOrders, billings]);

  /* ============================================================
     FILTERED ACCOUNTS — filtered across ALL work orders
  ============================================================ */
  const filteredAccounts = useMemo(() => {
    return workOrderAccounts.filter((wo) => {
      // 1. Status Filter
      if (statusFilter !== "All" && wo.label !== statusFilter) return false;

      // 2. Project Filter
      if (projectFilter) {
        const pId = wo.info?.project?._id || (typeof wo.info?.project === "string" ? wo.info.project : null);
        if (String(pId) !== String(projectFilter)) return false;
      }

      // 3. Work Order Filter
      if (workOrderFilter && String(wo.workOrder) !== String(workOrderFilter)) return false;

      // 4. Active PO filter (selected from PO dropdown)
      if (activePoFilter) {
        return String(wo.workOrder) === String(activePoFilter.workOrderId);
      }

      // 5. Search Text Filter (matches WO No, Label, Project, Client, or matched PO)
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const woNo = String(wo.info?.woNo || wo.info?.woNumber || "").toLowerCase();
        const label = String(wo.info?.label || "").toLowerCase();
        const projectName = String(wo.info?.project?.projectName || wo.info?.projectName || "").toLowerCase();
        const projectCode = String(wo.info?.project?.projectCode || "").toLowerCase();
        const clientName = String(wo.info?.client?.companyName || wo.info?.client?.name || "").toLowerCase();

        const matches =
          woNo.includes(q) ||
          label.includes(q) ||
          projectName.includes(q) ||
          projectCode.includes(q) ||
          clientName.includes(q) ||
          poMatchedWoIds.has(String(wo.workOrder));

        if (!matches) return false;
      }

      return true;
    });
  }, [workOrderAccounts, statusFilter, projectFilter, workOrderFilter, activePoFilter, search, poMatchedWoIds]);

  /* ============================================================
     PAGINATION ON FILTERED LIST
  ============================================================ */
  const totalCount = filteredAccounts.length;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 1;

  const paginatedAccounts = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredAccounts.slice(start, start + PAGE_SIZE);
  }, [filteredAccounts, currentPage]);

  const paginationItems = useMemo(() => {
    if (totalPages <= 1) return [];

    const delta = 2;
    const start = Math.max(2, currentPage - delta);
    const end   = Math.min(totalPages - 1, currentPage + delta);
    const items = [];

    items.push(
      <Pagination.Item key={1} active={currentPage === 1} onClick={() => setCurrentPage(1)}>
        1
      </Pagination.Item>
    );

    if (start > 2) items.push(<Pagination.Ellipsis key="s-ellipsis" disabled />);

    for (let p = start; p <= end; p++) {
      items.push(
        <Pagination.Item key={p} active={currentPage === p} onClick={() => setCurrentPage(p)}>
          {p}
        </Pagination.Item>
      );
    }

    if (end < totalPages - 1) items.push(<Pagination.Ellipsis key="e-ellipsis" disabled />);

    if (totalPages > 1) {
      items.push(
        <Pagination.Item
          key={totalPages}
          active={currentPage === totalPages}
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  }, [currentPage, totalPages]);

  const statusVariant = (label) =>
    label === "Completed" ? "success" : label === "In Progress" ? "warning" : "danger";

  /* ============================================================
     UI
  ============================================================ */
  return (
    <>
      <div className="mb-3">
        <PageTitle activeMenu="Accounts" motherMenu="Commercial" />
      </div>

      {/* ===== FILTER + ACTION BUTTONS IN EXACTLY ONE ROW ===== */}
      <div className="d-flex align-items-end justify-content-between gap-2 mb-4">
        {/* Left side: Filter Controls (Search, Project, Work Order, Status, Reset) */}
        <div className="d-flex align-items-end gap-2 flex-grow-1" style={{ minWidth: 0 }}>
          {/* Search */}
          <div style={{ flex: "1 1 140px", minWidth: "110px" }}>
            <Form.Label className="small fw-semibold mb-1">Search</Form.Label>
            <div className="position-relative" ref={poDropdownRef}>
              <Form.Control
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handlePoSearchSubmit()}
                autoComplete="off"
              />
              {searchingPo && (
                <Spinner
                  animation="border"
                  size="sm"
                  className="text-primary position-absolute"
                  style={{
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 5,
                  }}
                />
              )}

              {/* Live PO Search Dropdown Results */}
              {showPoDropdown && poSuggestions.length > 0 && (
                <div
                  className="position-absolute w-100 bg-white border rounded shadow-lg mt-1 p-0"
                  style={{
                    zIndex: 1050,
                    maxHeight: "340px",
                    overflowY: "auto",
                    minWidth: "280px",
                    borderRadius: "8px",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  }}
                >
                  <div className="p-2 px-3 bg-light border-bottom small fw-bold text-muted d-flex justify-content-between align-items-center">
                    <span>Matched POs ({poSuggestions.length})</span>
                    <button
                      type="button"
                      className="btn-close btn-close-sm"
                      style={{ fontSize: "0.6rem" }}
                      onClick={() => setShowPoDropdown(false)}
                    />
                  </div>
                  {poSuggestions.map((po) => {
                    const woInfo = po.workOrder || {};
                    const woTitle = woInfo.woNo || woInfo.woNumber || "Work Order";
                    const paid = Number(po.paidAmount || 0);
                    const total = Number(po.totalAmount || 0);
                    const pStatus =
                      po.paymentStatus ||
                      (paid >= total && total > 0
                        ? "Completed"
                        : paid > 0
                        ? "In Progress"
                        : "Pending");

                    return (
                      <div
                        key={po._id}
                        className="p-2 px-3 border-bottom text-start"
                        style={{ cursor: "pointer", transition: "all 0.15s ease-in-out" }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f0f7ff";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#fff";
                        }}
                        onClick={() => handleSelectPo(po)}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <strong className="text-primary" style={{ fontSize: "0.85rem" }}>
                            {po.poNumber}
                          </strong>
                          <Badge bg={statusVariant(pStatus)} style={{ fontSize: "0.68rem" }}>
                            {po.paymentStatus ||
                              (paid >= total && total > 0
                                ? "Paid"
                                : paid > 0
                                ? "Partially Paid"
                                : "Pending")}
                          </Badge>
                        </div>
                        <div className="small text-dark fw-semibold mt-1">
                          <i className="fas fa-briefcase text-secondary me-1"></i>
                          WO: {woTitle} {woInfo.label ? `(${woInfo.label})` : ""}
                        </div>
                        <div className="small text-muted d-flex justify-content-between mt-1">
                          <span className="text-truncate" style={{ maxWidth: "160px" }}>
                            {po.vendor?.name || po.vendor?.companyName || "Vendor"}
                          </span>
                          <strong className="text-success">{money(po.totalAmount)}</strong>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Project */}
          <div style={{ flex: "1 1 150px", minWidth: "120px" }}>
            <Form.Label className="small fw-semibold mb-1">Project</Form.Label>
            <Form.Select
              value={projectFilter}
              onChange={(e) => {
                setProjectFilter(e.target.value);
                setWorkOrderFilter("");
                setCurrentPage(1);
              }}
            >
              <option value="">All Projects</option>
              {projects.map(([pId, pName]) => (
                <option key={pId} value={pId}>
                  {pName}
                </option>
              ))}
            </Form.Select>
          </div>

          {/* Work Order */}
          <div style={{ flex: "1 1 150px", minWidth: "120px" }}>
            <Form.Label className="small fw-semibold mb-1">Work Order</Form.Label>
            <Form.Select
              value={workOrderFilter}
              onChange={(e) => {
                setWorkOrderFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Work Orders</option>
              {availableWorkOrders.map((w) => (
                <option key={w._id} value={w._id}>
                  {w.label ? `${w.label} (${w.woNo || w.woNumber})` : (w.woNo || w.woNumber || "WO")}
                </option>
              ))}
            </Form.Select>
          </div>

          {/* Payment Status */}
          <div style={{ width: "115px", flexShrink: 0 }}>
            <Form.Label className="small fw-semibold mb-1">Payment Status</Form.Label>
            <Form.Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="All">All</option>
              <option value="Completed">Completed</option>
              <option value="In Progress">In Progress</option>
              <option value="Pending">Pending</option>
            </Form.Select>
          </div>

          {/* Reset Filters */}
          {(projectFilter || workOrderFilter || statusFilter !== "All" || search || activePoFilter) && (
            <div style={{ flexShrink: 0 }}>
              <Button
                variant="outline-secondary"
                className="btn-input-height d-flex align-items-center justify-content-center shadow-sm"
                style={{ borderRadius: "1rem", width: "3rem", minWidth: "3rem" }}
                title="Reset Filters"
                onClick={handleResetAllFilters}
              >
                <i className="fas fa-undo"></i>
              </Button>
            </div>
          )}
        </div>

        {/* Right side: Three-Dot Dropdown Menu for Actions */}
        <div className="d-flex align-items-center flex-shrink-0">
          <Dropdown align="end">
            <Dropdown.Toggle
              variant="outline-secondary"
              className="btn-input-height d-flex align-items-center justify-content-center shadow-sm i-false"
              style={{ borderRadius: "1rem", width: "3rem", minWidth: "3rem" }}
              id="accounts-header-menu"
              title="More Options"
            >
              <i className="fas fa-ellipsis-v"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu className="shadow-lg border-0 py-2" style={{ minWidth: "220px", borderRadius: "12px" }}>
              <Dropdown.Item
                className="d-flex align-items-center py-2 px-3"
                onClick={() => navigate("/other-purchase-order-payments")}
              >
                <i className="fas fa-file-invoice-dollar text-primary me-2 fs-6"></i>
                <span className="fw-semibold">Other PO Payments</span>
              </Dropdown.Item>

              <Dropdown.Item
                className="d-flex align-items-center py-2 px-3"
                onClick={() => navigate("/debit-office")}
              >
                <i className="fas fa-building text-info me-2 fs-6"></i>
                <span className="fw-semibold">Office Expense</span>
              </Dropdown.Item>

              <CanAccess permission="read:reconciliations">
                <Dropdown.Item
                  className="d-flex align-items-center py-2 px-3"
                  onClick={() => navigate("/gst-reconciliation")}
                >
                  <i className="fas fa-calculator text-success me-2 fs-6"></i>
                  <span className="fw-semibold">GST Reconciliation</span>
                </Dropdown.Item>
              </CanAccess>

              <Dropdown.Item
                className="d-flex align-items-center py-2 px-3"
                onClick={() => navigate("/salary-accounts")}
              >
                <i className="fas fa-users text-danger me-2 fs-6"></i>
                <span className="fw-semibold">Staff Salary</span>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {/* Active PO Filter Banner */}
      {activePoFilter && (
        <div className="alert alert-primary d-flex justify-content-between align-items-center mb-3 py-2 px-3 shadow-sm border-0 rounded flex-wrap gap-2">
          <div>
            <i className="fas fa-filter text-primary me-2"></i>
            Filtered by PO: <strong className="text-dark">{activePoFilter.poNumber}</strong>
            {activePoFilter.vendorName && <span className="text-muted ms-2">({activePoFilter.vendorName})</span>}
            <Badge bg="white" text="primary" className="border ms-2">
              WO: {activePoFilter.workOrderNo} {activePoFilter.workOrderLabel ? `(${activePoFilter.workOrderLabel})` : ""}
            </Badge>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <Button
              size="sm"
              variant="primary"
              className="text-nowrap"
              onClick={() => navigate(`/purchase-order-payments?workOrder=${activePoFilter.workOrderId}&search=${encodeURIComponent(activePoFilter.poNumber)}`)}
            >
              <i className="fas fa-external-link-alt me-1"></i> Open PO Payment
            </Button>
            <Button size="sm" variant="outline-danger" onClick={clearPoSearch}>
              Clear Filter
            </Button>
          </div>
        </div>
      )}

      {/* ===== CONTENT ===== */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner />
        </div>
      ) : (
        <>
          {/* Record count info */}
          {totalCount > 0 && (
            <div className="text-muted small mb-3">
              Showing{" "}
              <strong>
                {(currentPage - 1) * PAGE_SIZE + 1}–
                {Math.min(currentPage * PAGE_SIZE, totalCount)}
              </strong>{" "}
              of <strong>{totalCount}</strong> work orders
            </div>
          )}

          {paginatedAccounts.length === 0 ? (
            <div className="text-center text-muted py-5">
              No work orders found for the selected filter.
            </div>
          ) : (
            <Row className="g-3">
              {paginatedAccounts.map((wo) => {
                const info = wo.info;
                if (!info) return null;

                return (
                  <Col key={wo.workOrder} md={6} xl={4}>
                    <Card className={`h-100 shadow-sm ${activePoFilter?.workOrderId === String(wo.workOrder) ? "border-primary border-2 shadow" : ""}`}>
                      <Card.Body>
                        {/* HEADER */}
                        {activePoFilter?.workOrderId === String(wo.workOrder) && (
                          <div className="mb-2">
                            <Badge bg="primary" className="d-inline-flex align-items-center">
                              <i className="fas fa-check-circle me-1"></i> Matches PO: {activePoFilter.poNumber}
                            </Badge>
                          </div>
                        )}
                        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <h6 className="fw-bold mb-1 text-primary" style={{ lineHeight: 1.2 }}>
                              {info?.label || info?.woNo || info?.woNumber || "Work Order"}
                            </h6>
                            <div className="text-muted small mb-1" style={{ fontWeight: 600 }}>
                              <i className="fas fa-sitemap me-1 opacity-60"></i>
                              {info?.woNo || info?.woNumber || "—"}
                            </div>
                            <div className="text-dark fw-medium small mb-1">
                              <i className="fas fa-project-diagram me-1 text-muted"></i>
                              {info?.project?.projectName || "—"}
                              {info?.project?.projectCode && (
                                <span className="text-muted ms-1">({info.project.projectCode})</span>
                              )}
                            </div>
                            {(info?.client?.companyName || info?.client?.name) && (
                              <div className="text-muted small mb-1">
                                <i className="fas fa-building me-1 opacity-75"></i>
                                {info.client?.companyName || info.client?.name}
                              </div>
                            )}
                          </div>

                          <div className="d-flex flex-column align-items-end gap-1 flex-shrink-0">
                            {info?.client && (
                              <div
                                title={info.client?.companyName || info.client?.name || "Client"}
                                style={{
                                  width: 38,
                                  height: 38,
                                  borderRadius: "8px",
                                  border: "1px solid #e9ecef",
                                  background: "#f8f9fa",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  overflow: "hidden",
                                  flexShrink: 0,
                                }}
                              >
                                {info.client?.image ? (
                                  <img
                                    src={getFileUrl(info.client.image)}
                                    alt=""
                                    style={{ width: "100%", height: "100%", objectFit: "contain", padding: 2 }}
                                  />
                                ) : (
                                  <i className="fas fa-building text-muted"></i>
                                )}
                              </div>
                            )}
                            <Badge bg={statusVariant(wo.label)}>{wo.label}</Badge>
                          </div>
                        </div>

                        {/* PROGRESS */}
                        <div className="d-flex justify-content-center mt-3">
                          <div
                            className="circular-progress"
                            style={{
                              "--value": wo.percent,
                              "--progress-color":
                                wo.label === "Completed"
                                  ? "#198754"
                                  : wo.label === "In Progress"
                                  ? "#ffc107"
                                  : "#dc3545",
                            }}
                          >
                            <span>{wo.percent}%</span>
                          </div>
                        </div>

                        {/* ACCOUNTS ACTIONS */}
                        <div className="mt-3">
                          <div className="small fw-semibold text-uppercase text-muted mb-1">
                            Accounts
                          </div>

                          <div className="d-flex gap-2 flex-wrap">
                            {/* Purchase / Service Invoice — purple */}
                            <Button
                              size="sm"
                              variant=""
                              style={{ borderColor: "#6f42c1", color: "#6f42c1", border: "1px solid #6f42c1", backgroundColor: "transparent" }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#6f42c1"; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#6f42c1"; }}
                              onClick={() => navigate(`/purchase-order-payments?workOrder=${wo.workOrder}`)}
                            >
                              Purchase / Service Invoice
                            </Button>


                            {/* Vendor Payment — amber */}
                            <Button
                              size="sm"
                              variant=""
                              style={{ borderColor: "#ffc107", color: "#ffc107", border: "1px solid #ffc107", backgroundColor: "transparent" }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#ffc107"; e.currentTarget.style.color = "#000"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#ffc107"; }}
                              onClick={() => navigate(`/vendor-payments?workOrder=${wo.workOrder}`)}
                            >
                              Vendor Payment
                            </Button>

                            {/* Contract Payment — cyan */}
                            <Button
                              size="sm"
                              variant=""
                              style={{ borderColor: "#0dcaf0", color: "#0dcaf0", border: "1px solid #0dcaf0", backgroundColor: "transparent" }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#0dcaf0"; e.currentTarget.style.color = "#000"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#0dcaf0"; }}
                              onClick={() => navigate(`/contractor-payments?workOrder=${wo.workOrder}`)}
                            >
                              Contract Payment
                            </Button>

                            {/* Credit — green */}
                            <Button
                              size="sm"
                              variant=""
                              style={{ borderColor: "#198754", color: "#198754", border: "1px solid #198754", backgroundColor: "transparent" }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#198754"; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#198754"; }}
                              onClick={() => navigate(`/billings?workOrder=${wo.workOrder}`)}
                            >
                              Credit
                            </Button>

                            {/* Debit — red */}
                            <Button
                              size="sm"
                              variant=""
                              style={{ borderColor: "#dc3545", color: "#dc3545", border: "1px solid #dc3545", backgroundColor: "transparent" }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#dc3545"; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#dc3545"; }}
                              onClick={() => navigate(`/debit?workOrder=${wo.workOrder}`)}
                            >
                              Debit
                            </Button>

                            {/* Received Payment — teal */}
                            <Button
                              size="sm"
                              variant=""
                              style={{ borderColor: "#20c997", color: "#20c997", border: "1px solid #20c997", backgroundColor: "transparent" }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#20c997"; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#20c997"; }}
                              onClick={() => navigate(`/billings?workOrder=${wo.workOrder}&paymentStatus=Paid`)}
                            >
                              Received Payment
                            </Button>

                            {/* Pending Payment — orange */}
                            <Button
                              size="sm"
                              variant=""
                              style={{ borderColor: "#fd7e14", color: "#fd7e14", border: "1px solid #fd7e14", backgroundColor: "transparent" }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#fd7e14"; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#fd7e14"; }}
                              onClick={() => navigate(`/billings?workOrder=${wo.workOrder}&paymentStatus=Submitted`)}
                            >
                              Pending Payment
                            </Button>

                            {/* Fixed / Recurring Ex — indigo */}
                            <Button
                              size="sm"
                              variant=""
                              style={{ borderColor: "#6610f2", color: "#6610f2", border: "1px solid #6610f2", backgroundColor: "transparent" }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#6610f2"; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#6610f2"; }}
                              onClick={() => navigate(`/debit?workOrder=${wo.workOrder}&expenseType=Fixed`)}
                            >
                              Fixed / Recurring Ex
                            </Button>

                            {/* Other Ex — pink */}
                            <Button
                              size="sm"
                              variant=""
                              style={{ borderColor: "#e83e8c", color: "#e83e8c", border: "1px solid #e83e8c", backgroundColor: "transparent" }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#e83e8c"; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#e83e8c"; }}
                              onClick={() => navigate(`/debit?workOrder=${wo.workOrder}&expenseType=Other`)}
                            >
                              Other Ex
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}

          {/* ===== PAGINATION ===== */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
              <Pagination className="mb-0">
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                />
                {paginationItems}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                />
              </Pagination>
              <span className="text-muted small">
                Page {currentPage} of {totalPages}
              </span>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default Accounts;
